import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  RETRACTED_ARM_LENGTHS,
  CLEARANCE_RULES,
  SUBSTRATE_ANCHORS,
  OBSTRUCTIONS,
} from '../data/measuringGuide';
import './MeasuringGuidePage.css';
import { BRACKET_COUNT } from '../config/installation';

// Six numbered steps, each with a diagram slot beside it. Diagrams are optional:
// a step whose image is missing simply renders without one, so the page is
// useful before the artwork exists.
const STEP_IMAGE = (n) => `/images/measuring/step-0${n}.webp`;

function Step({ n, title, image, imageAlt, children }) {
  return (
    <section className="mg-step">
      <div className="mg-step-body">
        <p className="mg-step-num">Step {n}</p>
        <h2 className="mg-step-title">{title}</h2>
        {children}
      </div>
      <div className="mg-step-figure">
        {/* Diagram slot. If the file is not there the browser shows the empty
            frame rather than a broken-image icon, because alt text plus the
            styled container carry it. */}
        <img src={image} alt={imageAlt} loading="lazy" width={720} height={540}
             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </div>
    </section>
  );
}

export default function MeasuringGuidePage() {
  return (
    <div className="mg-page">
      <SEO
        title="How to Measure for Your Awning"
        description="How to measure for a retractable awning - width at mounting height, projection and fall, headroom, wall type and obstructions, plus the arm length table."
        keywords="how to measure awning, awning measurements, retractable awning width, awning projection, awning installation clearances"
        canonical="/measuring-guide"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Measuring Guide' }]), SEO.webPageSchema({ type: 'WebPage', name: 'How to measure for your awning', description: 'How to measure your space for a retractable awning.', url: '/measuring-guide' })]}
      />

      <section className="mg-hero">
        <div className="mg-hero-content">
          <p className="mg-label">Measuring Guide</p>
          <h1 className="mg-title">How to measure for your awning.</h1>
          <p className="mg-subtitle">
            We supply the awning and you arrange the installer, so getting the measurements right
            up front is what makes the whole thing work. Ten minutes with a tape measure and a
            phone camera is usually all it takes.
          </p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Measuring Guide' }]} />

      <div className="mg-body">
        <div className="mg-inner">

          <div className="mg-callout">
            <p>
              <strong>Not sure?</strong> Send us your photos and rough measurements and we will
              work it through with you before you order. Final measurements remain your
              responsibility, so have your installer confirm them before you commit.
            </p>
          </div>

          <Step
            n={1}
            title="Measure your available width at the mounting height"
            image={STEP_IMAGE(1)}
            imageAlt="Diagram of a wall with a tape measure held at the awning mounting height rather than at ground level"
          >
            <p>
              Measure the clear width of the wall <strong>at the exact height the awning will be
              mounted</strong>, not at ground level. Walls are rarely clear the whole way up:
              downpipes, light fittings, gutters, meter boxes and window heads all reduce the
              usable width at head height even when the ground looks open.
            </p>
            <p>Write this figure down. Everything else is checked against it.</p>

            <div className="mg-formula">
              <p className="mg-formula-label">Minimum installable width</p>
              <p className="mg-formula-body">
                (left base + retracted arm length) + (right base + retracted arm length) + 16 cm
              </p>
              <p className="mg-formula-note">
                The retracted arm length depends on the projection you choose - see step 2. If your
                available width is less than this figure, the awning will not fit at that
                projection.
              </p>
            </div>

            <div className="mg-rules">
              <p className="mg-rules-title">Clearances</p>
              <ul>
                {CLEARANCE_RULES.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </Step>

          <Step
            n={2}
            title="Work out your projection, and check the fall"
            image={STEP_IMAGE(2)}
            imageAlt="Side-on diagram showing the awning extended, with the front bar sitting lower than the cassette"
          >
            <p>
              Projection is how far the awning reaches out from the wall. The longer the
              projection, the longer the arms are when folded away - which is what drives the
              minimum width in step 1.
            </p>

            <div className="mg-table-wrap">
              <table className="mg-table">
                <thead>
                  <tr><th>Arm projection</th><th>Retracted arm length</th></tr>
                </thead>
                <tbody>
                  {RETRACTED_ARM_LENGTHS.map((r) => (
                    <tr key={r.projection}><td>{r.projection}</td><td>{r.retracted}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>
              <strong>Check the fall.</strong> An awning is pitched, so it drops as it extends: the
              front bar sits <strong>lower than the cassette</strong>. Measure the height at the
              front bar at full projection, not just at the wall, and make sure paths, doors and
              windows stay clear.
            </p>
          </Step>

          <Step
            n={3}
            title="Check mounting height, headroom and door swing"
            image={STEP_IMAGE(3)}
            imageAlt="Diagram showing mounting height above a door, headroom under the front bar, and the arc of a door swinging open"
          >
            <p>
              Decide the mounting height, then check the clearance at the front bar{' '}
              <strong>at full extension</strong> before anything is fixed. Because the awning falls
              as it extends, the lowest point is at the front bar, furthest from the wall.
            </p>
            <ul>
              <li>Will people walk under it comfortably at the front edge?</li>
              <li>Do doors and windows still open fully, including outward-swinging and bi-fold doors?</li>
              <li>Is there room above for the cassette itself, clear of the gutter or eave?</li>
            </ul>
          </Step>

          <Step
            n={4}
            title="Identify your wall type"
            image={STEP_IMAGE(4)}
            imageAlt="Diagram comparing a bracket anchored correctly into solid masonry against one pulling out of a cavity wall fitted with the wrong anchor"
          >
            <p>
              This is the step people skip, and it is the one that causes failures. The awning is
              heavy and everything hangs off {BRACKET_COUNT}, so the anchor at each one has to reach
              something that can carry it. Your wall type decides which anchor that is.
            </p>
            <div className="mg-rules">
              <p className="mg-rules-title">Which anchor for which wall</p>
              <ul>
                {SUBSTRATE_ANCHORS.map((r, i) => (
                  <li key={i}><strong>{r.wall}</strong> - {r.anchor}</li>
                ))}
              </ul>
            </div>
            <p>
              The diagram shows the failure to avoid: the same bracket, fitted with a solid-wall
              anchor into a cavity wall, pulls out under load. The right anchor for the wall is a
              few dollars at any hardware store.
            </p>
          </Step>

          <Step
            n={5}
            title="Check for obstructions"
            image={STEP_IMAGE(5)}
            imageAlt="Diagram of a wall marked with common obstructions including a downpipe, gutter, light fitting, meter box and air conditioning unit"
          >
            <p>
              Walk the wall at mounting height and note anything in the way. Each of these either
              reduces your usable width or needs relocating before installation:
            </p>
            <ul className="mg-obstructions">
              {OBSTRUCTIONS.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
            <p>
              Photograph anything you are unsure about. It is far cheaper to shift a downpipe on
              paper than after the awning has been made to a width that does not fit.
            </p>
          </Step>

          <Step
            n={6}
            title="What to send us"
            image={STEP_IMAGE(6)}
            imageAlt="Diagram showing the three photographs to send: a straight-on view of the wall, an angled view, and a close-up of the mounting area"
          >
            <p><strong>Photos</strong> - taken in daylight, with the whole area visible:</p>
            <ul>
              <li>One straight on to the wall, standing back far enough to see the full width</li>
              <li>One from each side at an angle, showing corners, downpipes and the eave</li>
              <li>A close-up of the mounting area, showing the wall surface and anything fixed to it</li>
              <li>A photo of anything you are unsure about, with a tape measure in shot for scale</li>
            </ul>
            <p><strong>Measurements</strong> - in centimetres or millimetres, stated at mounting height:</p>
            <ul>
              <li>Available width at the mounting height</li>
              <li>Distance to the nearest corner at each end</li>
              <li>Intended projection</li>
              <li>Mounting height from the ground, and the height of any door or window head below it</li>
              <li>What the wall is made of, if you know</li>
            </ul>
          </Step>

          <div className="mg-cta">
            <Link to="/request-quote" className="mg-btn-primary">Send your photos and measurements</Link>
            <Link to="/colours" className="mg-btn-secondary">View colours</Link>
          </div>

          <p className="mg-footnote">
            Figures on this page are taken from the installation manual supplied with the awning.
            Where this guide and the manual differ, the manual applies. ASG is supply only - we do
            not attend or assess sites, and responsibility for final measurements and for the
            suitability of the structure rests with you and your installer.{' '}
            <Link to="/for-installers">Information for your installer</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
